import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const CartContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Générer ou récupérer le session_id (stocké dans localStorage pour persistance)
const getSessionId = () => {
  let sessionId = localStorage.getItem('cart_session');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem('cart_session', sessionId);
  }
  return sessionId;
};

// Helper pour créer les headers avec le session_id
const getCartHeaders = (sessionId) => ({
  'X-Cart-Session': sessionId,
  'Content-Type': 'application/json'
});

export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [], items_count: 0, subtotal: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sessionId = getSessionId();

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/cart`, {
        headers: getCartHeaders(sessionId)
      });
      setCart(response.data);
      setError(null);
    } catch (err) {
      console.error('Erreur fetch cart:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const addToCart = async (productId, quantity = 1, variantId = null, metadata = null) => {
    try {
      setLoading(true);
      const payload = { 
        product_id: productId, 
        quantity, 
        variant_id: variantId 
      };
      // Ajouter les métadonnées (couleur, etc.) si présentes
      if (metadata) {
        payload.metadata = metadata;
      }
      await axios.post(`${API_URL}/api/cart/items`, 
        payload,
        { headers: getCartHeaders(sessionId) }
      );
      await fetchCart();
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      return { success: false, error: err.response?.data?.detail || err.message };
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemIndex, quantity) => {
    try {
      setLoading(true);
      await axios.patch(`${API_URL}/api/cart/items/${itemIndex}`,
        { quantity },
        { headers: getCartHeaders(sessionId) }
      );
      await fetchCart();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (itemIndex) => {
    try {
      setLoading(true);
      await axios.delete(`${API_URL}/api/cart/items/${itemIndex}`, {
        headers: getCartHeaders(sessionId)
      });
      await fetchCart();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      error,
      addToCart,
      updateQuantity,
      removeItem,
      refreshCart: fetchCart,
      sessionId
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
